import { ClampToEdgeWrapping, DataTexture, LinearFilter, LinearMipmapLinearFilter, NearestFilter, NoColorSpace, RGBAFormat, RepeatWrapping, UnsignedByteType } from 'three';
import type { MaterialTextures } from '../materials/common';
import { paintBrushTexture } from './painter';
import { paintBarkNormal, paintGlitter, paintGroundNormal, paintLeafCluster, paintRockNormal, paintSandGrain, paintSandRipples } from './maps';

function dataTexture(data: Uint8Array, size: number, opts: { repeat?: boolean; nearest?: boolean; anisotropy?: number } = {}): DataTexture {
  const t = new DataTexture(data, size, size, RGBAFormat, UnsignedByteType);
  t.wrapS = t.wrapT = opts.repeat === false ? ClampToEdgeWrapping : RepeatWrapping;
  if (opts.nearest) {
    t.minFilter = NearestFilter;
    t.magFilter = NearestFilter;
    t.generateMipmaps = false;
  } else {
    t.minFilter = LinearMipmapLinearFilter;
    t.magFilter = LinearFilter;
    t.generateMipmaps = true;
  }
  t.anisotropy = opts.anisotropy ?? 8;
  t.colorSpace = NoColorSpace;
  t.needsUpdate = true;
  return t;
}

/**
 * Generate every material map procedurally (~100–200 ms). All maps are tileable data textures, so
 * nothing is downloaded and the same code runs on web, in workers and on expo-gl.
 */
export function createWorldTextures(): MaterialTextures {
  return {
    brush: dataTexture(paintBrushTexture(256, 3), 256),
    leaf: dataTexture(paintLeafCluster(256, 7), 256, { repeat: false }),
    rippleShallow: dataTexture(paintSandRipples(512, false, 11), 512, { anisotropy: 16 }),
    rippleSteep: dataTexture(paintSandRipples(512, true, 12), 512, { anisotropy: 16 }),
    sandGrain: dataTexture(paintSandGrain(256, 13), 256),
    glitter: dataTexture(paintGlitter(256, 14), 256, { nearest: true }),
    bark: dataTexture(paintBarkNormal(256, 15), 256),
    rock: dataTexture(paintRockNormal(256, 16), 256),
    ground: dataTexture(paintGroundNormal(256, 17), 256),
  };
}
